import { cloneElement } from 'react';

import {
  SFStockoutItemChip,
  SubstitutionFlowDeleteItemBtn,
} from 'features/order/substitution-flow';

import { useSubstitutionFlowToppingHOC } from './hooks';

import makeClasses from './SubstitutionFlowToppingHOC.style';

import type { ReactElement, ReactNode } from 'react';
import type {
  CustomizationId,
  OrderId,
  ProductId,
  StoreId,
} from 'shared/domain';

export function SubstitutionFlowToppingHOC(props: Props) {
  const {
    orderId,
    orderProductId,
    customizationId,
    storeId,
    children,
    isBeingManaged,
  } = props;

  const {
    hasReplacement,
    stockoutItem,
    replacementItem,
    formattedPrice,
    isRemovable,
  } = useSubstitutionFlowToppingHOC({
    orderId,
    orderProductId,
    customizationId,
  });

  const _classes = makeClasses();

  const showStockoutToppingChip = !!stockoutItem;
  const showDeleteItemButton = isRemovable && isBeingManaged;
  if (!showStockoutToppingChip) return <>{children}</>;
  return (
    <article
      className={_classes.article}
      aria-label='substitution-flow-topping-hoc'
      data-topping-id={customizationId}
      data-has-replacement={hasReplacement}
      data-order-product-id={stockoutItem.orderProductId}
    >
      {hasReplacement && (
        <section
          aria-label='replacement-topping'
          data-topping-id={replacementItem?.id}
        >
          <header className={_classes.toppingHeader}>
            <h2>
              <span>{`${replacementItem?.units}x `}</span>
              {replacementItem?.name}
              <SFStockoutItemChip mode='replacement' />
            </h2>
            <span>{formattedPrice}</span>
          </header>
        </section>
      )}

      <section
        aria-label='stockout-topping'
        className={_classes.stockoutSection}
        data-topping-id={customizationId}
        data-has-replacement={hasReplacement}
        data-product-id={stockoutItem.productId}
      >
        {cloneElement(children as ReactElement, { showStockoutToppingChip })}
        {showDeleteItemButton && (
          <div className={_classes.deleteItemButtonContainer}>
            <SubstitutionFlowDeleteItemBtn
              orderId={orderId}
              storeId={storeId}
              orderProductId={orderProductId}
              itemId={customizationId}
              isProduct={false}
            />
          </div>
        )}
      </section>
    </article>
  );
}

interface Props {
  orderId: OrderId;
  orderProductId: ProductId;
  customizationId: CustomizationId;
  storeId: StoreId;
  children: ReactNode;
  isBeingManaged: boolean;
}
